import { useLoaderData, useParams } from "react-router-dom";
import { useAtomValue, useSetAtom, useAtom } from "jotai";
import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useContext,
} from "react";
import {
  type BuildData,
  type LoaderApp,
  type Test,
  type UnparsedTest,
  type Dependency,
  type SessionConfigData,
  type CompanyData,
} from "@/utils/types";
import styled from "@emotion/styled";

import { testEditorPropertiesAtom } from "@/atoms/test-editor-atom";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import {
  customFirestore,
  customStorage,
  tipTapCollabJWTGenerator,
} from "@/firebase";
import { atomWithReset, RESET } from "jotai/utils";
import {
  bufferToBase64,
  getTipTapExtensions,
  getTipTapSchema,
  trackSentryException,
} from "@/utils/helpers";

import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  appetizeClientAtom,
  appetizeSessionStore,
  buildLogsAtomWithId,
} from "@/atoms";
import { DeepLinkDialog } from "@/components/deeplinkDialog";
import ScreenRotationIcon from "@mui/icons-material/ScreenRotation";
import LocationSearchingIcon from "@mui/icons-material/LocationSearching";
import { getDownloadURL, ref } from "firebase/storage";
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CropScreenshotDialog, {
  CropScreenshotDialogStateAtom,
} from "../components/cropScreenshotDialog";
import {
  Autorenew,
  Camera,
  CameraAlt,
  Home,
  RadioButtonChecked,
  Storage,
} from "@mui/icons-material";
import { useTestRecorder } from "@/components/test-editor-gpt/test-recorder/use-test-recorder";
import RecordingOverlay from "@/components/test-editor-gpt/test-recorder/RecordingOverlay";
import RecordingPreviewDialog, {
  type RecordingPayload,
} from "@/components/test-editor-gpt/test-recorder/RecordingPreviewDialog";
import { Node } from "prosemirror-model";
import TestEditorGpt from "../components/test-editor-gpt/test_editor_gpt";
import { generateJSON } from "@tiptap/react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import AlertTitle from "@mui/material/AlertTitle";
import { companyDataAtom } from "./test-run/test-run-atoms";
import { kAndroidOsName } from "@/constants/appetize-constants";
import buildDataAtom from "../atoms/buildDataAtom";
import { DeviceProvider } from "./sessions/DeviceContext";
import { AuthContext } from "@/auth/AuthContext";
import { customAlertStateAtom } from "@/components/custom-alert";
import { sessionMutationAtom } from "@/utils/simulator";
import DialogActions from "@mui/material/DialogActions";
import DeviceWrapper from "@/components/device-provider/device-provider";
import { useStore } from "@tanstack/react-store";

interface DeviceContextState {
  uiInspectorEnabled: boolean;
  recordInteractionsEnabled: boolean;
}

const TestBuilderPage = ({
  markdownMode = false,
}: {
  markdownMode?: boolean;
} = {}) => {
  const { buildId } = useParams();
  const { testId } = useParams();
  const setTestEditorProperties = useSetAtom(testEditorPropertiesAtom);
  const queryClient = useQueryClient();
  const { user } = useContext<any>(AuthContext);
  const setCustomAlertState = useSetAtom(customAlertStateAtom);
  const [deviceContextState, setDeviceContextState] =
    useState<DeviceContextState>({
      uiInspectorEnabled: false,
      recordInteractionsEnabled: false,
    });
  useState(false);

  useEffect(() => {
    return () => {
      appetizeSessionStore.setState((prev) => ({
        getData: () => null,
        startedAt: null,
      }));
      setTestEditorProperties(RESET);
      buildLogsAtomWithId.remove(buildId);
      queryClient.removeQueries({ queryKey: ["deviceScreen"] });
    };
  }, [user]);

  const {
    data: buildData,
    isLoading: isLoadingBuild,
    isError: isErrorBuild,
    error: errorBuild,
  } = useAtomValue(buildDataAtom({ buildId, organizationId: user.companyId }));

  useEffect(() => {
    if (buildData != null && buildId != buildData?.id) {
      const description =
        buildId == null
          ? "Since you haven't selected a build, we picked the last uploaded build for you"
          : "Since the build you selected doesn't exist, we picked the last uploaded build for you";
      const title =
        buildId == null ? "No build selected" : "Build doesn't exist";
      setCustomAlertState({
        open: true,
        type: "info",
        title,
        description,
        maintainDisplayed: true,
      });
    }
  }, [buildId, buildData]);

  const {
    data: companyData,
    isLoading: isLoadingCompany,
    isError: isErrorCompany,
    error: errorCompany,
  } = useAtomValue(companyDataAtom(buildData?.organisationId));

  useQuery({
    queryKey: ["physicalDeviceSwitch"],
    queryFn: async () => {
      console.log("physicalDeviceSwitch");
      const urlSearchParams = new URLSearchParams(location.search);

      const usePhysicalDeviceParam = urlSearchParams.get("usePhysicalDevice");
      if (
        usePhysicalDeviceParam &&
        companyData!.settings.enablePhysicalDeviceExecutionToggle
      ) {
        const decodedMatch = decodeURIComponent(usePhysicalDeviceParam);
        if (decodedMatch?.toLowerCase() === "true") {
          queryClient.setQueryData(
            ["getCompanyData", companyData!.organisationId],
            (prev: CompanyData) => ({
              ...prev,
              settings: { ...prev.settings, enableAppetizeCloneEditor: true },
            })
          );
        }
      } else if (
        companyData!.settings.enablePhysicalDeviceExecutionToggle &&
        companyData?.settings.enableAppetizeCloneEditor !==
          companyData?.settings.originalEnableAppetizeCloneEditor
      ) {
        void queryClient.setQueryData(
          ["getCompanyData", companyData!.organisationId],
          (prev: CompanyData) => ({
            ...prev,
            settings: {
              ...prev.settings,
              enableAppetizeCloneEditor:
                prev.settings.originalEnableAppetizeCloneEditor,
            },
          })
        );
      }
      return 0;
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    enabled: companyData != null && buildData != null,
  });

  const {
    isLoading: isLoadingTest,
    data: testData,
    isError: isErrorTest,
    error: errorTest,
  } = useQuery<Test | null>({
    queryKey: ["getTestForTestEditor", testId!],
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    gcTime: 0,
    enabled: companyData != null && buildData != null,
    queryFn: async ({ queryKey: [, id] }): Promise<Test | null> => {
      if (testId != null) {
        const docRef = doc(customFirestore, "custom-tests", testId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const unparsedTest: UnparsedTest = {
            ...(docSnap.data() as UnparsedTest),
            id: docSnap.id,
          };

          const parsedSettings = {
            launchParams: unparsedTest.settings?.launchParams ?? [],
            preRequests:
              unparsedTest.settings?.preRequests?.map((preRequest) => ({
                ...preRequest,
                delayBeforeRequest: preRequest.delayBeforeRequest ?? 0,
              })) ?? [],
            teardownRequests: unparsedTest.settings?.teardownRequests ?? [],
            inputKeys: unparsedTest.settings?.inputKeys ?? [],
            mediaFiles: unparsedTest.settings?.mediaFiles ?? [],
          };

          let parsedEnrichedInteractionsLogs = {
            promptGenerationScreenshots: {},
            enrichedInteractionLogs: {},
          } as {
            promptGenerationScreenshots: Record<string, string[]>;
            enrichedInteractionLogs: Record<
              string,
              Array<{
                enrichedLog: string;
                rawLog: string;
              }>
            >;
          };

          if (unparsedTest.enrichedInteractionsLogsPath != null) {
            const downloadUrl = await getDownloadURL(
              ref(customStorage, unparsedTest.enrichedInteractionsLogsPath)
            );

            const response = await axios.get(downloadUrl);
            const unparsedResponse = response.data as Record<
              string,
              Array<{
                screenshot: string;
                screenshotUrl?: string;
                enrichedLog: string;
                rawLog: string;
              }>
            >;

            const startTime = Date.now();
            parsedEnrichedInteractionsLogs = await Object.keys(
              unparsedResponse
            ).reduce(async (prev, current) => {
              const parsedIndex = parseInt(current);
              const awaitedPrev = await prev;
              return {
                promptGenerationScreenshots: {
                  ...awaitedPrev.promptGenerationScreenshots,
                  [parsedIndex]: await Promise.all(
                    unparsedResponse[current].map(async (enrichedLog) => {
                      if (enrichedLog.screenshotUrl != null) {
                        return enrichedLog.screenshotUrl;
                      }

                      return await getDownloadURL(
                        ref(customStorage, enrichedLog.screenshot)
                      );
                    })
                  ),
                },
                enrichedInteractionLogs: {
                  ...awaitedPrev.enrichedInteractionLogs,
                  [parsedIndex]: unparsedResponse[current].map(
                    (enrichedLog) => ({
                      enrichedLog: enrichedLog.enrichedLog,
                      rawLog: enrichedLog.rawLog,
                    })
                  ),
                },
              };
            }, Promise.resolve({ promptGenerationScreenshots: {}, enrichedInteractionLogs: {} }));
            console.log(
              `Finished awaiting. Waiting took`,
              (Date.now() - startTime) / 1000
            );
          }

          const subCollectionRef = collection(docRef, "dependencies");
          const subCollectionSnapshot = await getDocs(subCollectionRef);
          const parsedDependencies = subCollectionSnapshot.docs.map<Dependency>(
            (doc) => {
              return { ...(doc.data() as Dependency), id: doc.id };
            }
          );

          let prosemirrorContent: Node;
          const testCommands = unparsedTest.commands;

          const tipTapSchema = getTipTapSchema();
          try {
            prosemirrorContent = Node.fromJSON(
              tipTapSchema,
              JSON.parse(testCommands)
            );
          } catch (e) {
            trackSentryException(e);
            prosemirrorContent = Node.fromJSON(
              tipTapSchema,
              generateJSON(
                `<p>${testCommands.replaceAll("\n", "<br/>")}</p>`,
                getTipTapExtensions()
              )
            );
          }
          setTestEditorProperties((prev) => ({
            ...prev,
            originalTestCommands: prosemirrorContent,
            testCommands: prosemirrorContent,
            originalRecordingTitle: unparsedTest.title,
            recordingTitle: unparsedTest.title,
          }));

          return {
            id: unparsedTest.id,
            title: unparsedTest.title,
            commands: unparsedTest.commands,
            organisationId: unparsedTest.organisationId,
            dependencyInputValues: unparsedTest.dependencyInputValues,
            enrichedInteractionsLogsPath:
              unparsedTest.enrichedInteractionsLogsPath,
            folderId: unparsedTest.folderId,
            cachedSteps: unparsedTest.cachedSteps,
            enrichedInteractionsLogs:
              parsedEnrichedInteractionsLogs.enrichedInteractionLogs,
            promptGenerationScreenshots:
              parsedEnrichedInteractionsLogs.promptGenerationScreenshots,
            settings: parsedSettings,
            dependencies: parsedDependencies,
          };
        } else {
          throw new Error("Test doesn't exist");
        }
      }
      return null;
    },
  });

  const tipTapJWTQuery = useQuery({
    queryKey: ["tip-tap-jwt"],
    queryFn: async () => {
      const response = await tipTapCollabJWTGenerator();
      return response.data;
    },
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    gcTime: 300000,
  });

  useDocumentTitle(testData?.title ? `Test: ${testData.title}` : "Test");

  const isLoadingSimulator = useMemo(() => {
    return isLoadingBuild || isLoadingTest || isLoadingCompany;
  }, [isLoadingBuild, isLoadingTest, isLoadingCompany]);

  const isErrorSimulator = useMemo(() => {
    return isErrorTest || isErrorBuild || isErrorCompany;
  }, [isErrorTest, isErrorBuild, isErrorCompany]);

  const sessionConfig = useMemo<SessionConfigData | undefined>(() => {
    if (companyData == null || buildData == null) {
      return undefined;
    }

    const platform = buildData.platform!.toLocaleLowerCase();
    return {
      platform,
      os:
        platform === kAndroidOsName.toLowerCase()
          ? companyData.settings.defaultSessionConfig.osAndroid
          : companyData.settings.defaultSessionConfig.osIos,
      device:
        platform === kAndroidOsName.toLowerCase()
          ? companyData.settings.defaultSessionConfig.deviceAndroid
          : companyData.settings.defaultSessionConfig.deviceIos,
      language: companyData.settings.defaultSessionConfig.language,
      orientation: companyData.settings.defaultSessionConfig.orientation,
      region: companyData.settings.defaultSessionConfig.region,
    };
  }, [companyData, buildData]);

  return (
    <ContentWrapper>
      <SimulatorSection>
        <TestBuilderCustomAlert />
        {isLoadingSimulator && (
          <SpinnerWrapper>
            <CircularProgress />
          </SpinnerWrapper>
        )}

        {isErrorSimulator && (
          <p>
            Error:{" "}
            {errorBuild?.message ?? errorCompany?.message ?? errorTest?.message}
          </p>
        )}

        {/* if loading is finished, no error occured, and data is available display the test emulator */}
        {!isLoadingSimulator &&
          !isErrorSimulator &&
          buildData != null &&
          companyData != null && (
            <DeviceProvider
              settingsData={companyData.settings}
              deviceContextState={deviceContextState}
            >
              <SimulatorWrapper
                deviceContextState={deviceContextState}
                setDeviceContextState={setDeviceContextState}
                test={testData}
                buildData={buildData}
                companyData={companyData}
                sessionConfig={sessionConfig}
                markdownMode={markdownMode}
              />
            </DeviceProvider>
          )}
        <Overlay id="overlay">
          <ScanningLine />
        </Overlay>
      </SimulatorSection>

      <EditorSection>
        {!isLoadingSimulator &&
          !isErrorSimulator &&
          buildData != null &&
          companyData != null &&
          tipTapJWTQuery.data != null && (
            <TestEditorGpt
              test={testData}
              tipTapJWTData={tipTapJWTQuery.data}
              buildData={buildData}
              companyData={companyData}
              sessionConfig={sessionConfig!}
              markdownMode={markdownMode}
            />
          )}
      </EditorSection>
    </ContentWrapper>
  );
};

const SimulatorWrapper = ({
  test,
  buildData,
  companyData,
  sessionConfig,
  deviceContextState,
  setDeviceContextState,
  markdownMode = false,
}: {
  test: Test;
  buildData: BuildData;
  companyData: CompanyData;
  sessionConfig?: SessionConfigData;
  deviceContextState: DeviceContextState;
  setDeviceContextState: Dispatch<SetStateAction<DeviceContextState>>;
  markdownMode?: boolean;
}) => {
  const session = useStore(appetizeSessionStore, (state) => state);

  const loaderData = useLoaderData() as LoaderApp;
  const buildLogsData = useAtomValue(loaderData.buildLogsData);
  const videoFrames = buildLogsData.videoFrames;

  const setCropScreenshotDialogState = useSetAtom(
    CropScreenshotDialogStateAtom
  );
  const gptDriverSimulator = useAtomValue(sessionMutationAtom);
  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const { user } = useContext<any>(AuthContext);


  const saveScreenshot = useMutation({
    mutationKey: ["saveScreenshotForTemplate"],
    mutationFn: async () => {
      let base64Screenshot;
      if (companyData.settings.enableAppetizeCloneEditor) {
        base64Screenshot = await gptDriverSimulator.data?.getScreenshot();
      } else {
        const lastVideoFrame = videoFrames
          .filter((value) => value?.relativeTimestamp < Date.now())
          .at(-1);
        base64Screenshot = `data:image/jpeg;base64,${bufferToBase64(
          lastVideoFrame?.buffer
        )}`;
      }

      setCropScreenshotDialogState({
        open: true,
        imageSrc: base64Screenshot,
        companyData,
        deviceName: sessionConfig?.device ?? "--",
      });

      return base64Screenshot;
    },
    onError: (error) => {
      console.error("Error:", error);
      setCustomAlertState({
        open: true,
        type: "error",
        title: "Something went wrong",
        description: "An error occurred while taking screenshot",
        maintainDisplayed: false,
      });
    },
  });

  const rotateHandler = useCallback(() => {
    session.getData()?.rotate("left");
  }, [session]);

  const [openResetDialog, setOpenResetDialog] = useState(false);
  const handleOpenResetDialog = useCallback(() => {
    setOpenResetDialog(true);
  }, []);

  const goToOSHomeMutation = useMutation({
    mutationKey: ["goToOSHome"],
    mutationFn: async () => {
      if (companyData.settings.enableAppetizeCloneEditor) {
        await gptDriverSimulator.data?.commands.tapHomeScreen();
      } else {
        await session.getData()?.keypress("HOME");
      }
    },
    onError: (error) => {
      console.error(error);
      setCustomAlertState({
        open: true,
        type: "error",
        title: "Something went wrong",
        description: "An error occurred while going to OS home screen",
        maintainDisplayed: false,
      });
    },
  });

  const appetizeClient = useAtomValue(appetizeClientAtom);

  const startGptDriverSession = useCallback(() => {
    gptDriverSimulator.mutate({
      platform: sessionConfig!.platform,
      device: sessionConfig!.device,
      os: sessionConfig!.os,
      buildId: buildData.id,
      bundleId: buildData.bundle!,
      storagePath: buildData.storage_path,
      grantPermissions: companyData.settings.autoGrantPermissions,
      userId: user.uid ?? "",
      language:
        companyData.settings.defaultSessionConfig.language.split("_")[0],
      countryCode:
        companyData.settings.defaultSessionConfig.language.split("_")[1],
      proxy: companyData.settings.proxy,
      recordVideo: false,
      onCleanup: () => {
        gptDriverSimulator.reset();
      },
      organisationId: companyData.organisationId,
      launchParams: test?.settings?.launchParams ?? [],
      mediaFiles: test?.settings?.mediaFiles,
      usePhysicalDevice:
        new URLSearchParams(location.search).get("usePhysicalDevice")?.toLowerCase() === "true",
    });
  }, [buildData, companyData, sessionConfig, gptDriverSimulator, test, user]);

  useEffect(() => {
    if (gptDriverSimulator.error) {
      console.log("gptDriverSimulator.error", gptDriverSimulator.error);

      const errorMessage =
        gptDriverSimulator.error.message ?? "Unexpected error";
      setCustomAlertState({
        open: true,
        type: "error",
        title: "Something went wrong",
        description: errorMessage,
        maintainDisplayed: false,
      });
    }
  }, [gptDriverSimulator.error, setCustomAlertState]);

  const sessionResetHandler = useCallback(async () => {
    setOpenResetDialog(false);
    if (companyData.settings.enableAppetizeCloneEditor) {
      await gptDriverSimulator.data?.stop();
      startGptDriverSession();
    } else {
      await session.getData()?.end();
      await appetizeClient?.startSession();
    }
    setDeviceContextState({
      uiInspectorEnabled: false,
      recordInteractionsEnabled: false,
    });
  }, [
    session,
    appetizeClient,
    gptDriverSimulator,
    companyData,
    startGptDriverSession,
    setDeviceContextState,
  ]);

  const [activeSessionRunning, setActiveSessionRunning] = useState(false);
  const queryClient = useQueryClient();

  const [recording, setRecording] = useState<RecordingPayload | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const recorder = useTestRecorder({
    getSession: () =>
      companyData.settings.enableAppetizeCloneEditor
        ? ((gptDriverSimulator.data ?? null) as any)
        : session.getData(),
    onComplete: (result) => {
      setRecording({
        blob: result.blob,
        durationMs: result.durationMs,
        fps: result.fps,
        hasAudio: result.hasAudio,
      });
      setPreviewOpen(true);
    },
    onError: (message) => {
      setCustomAlertState({
        open: true,
        type: "error",
        title: "Recording failed",
        description: message,
        maintainDisplayed: false,
      });
    },
  });
  const isRecording = recorder.state === "recording";

  const appResetHandler = useCallback(() => {
    setOpenResetDialog(false);
    if (companyData.settings.enableAppetizeCloneEditor) {
      gptDriverSimulator.data?.launchApp();
    } else {
      session.getData?.()?.restartApp();
    }
  }, [session, companyData, gptDriverSimulator]);

  useEffect(() => {
    appetizeClient?.on("session", async (newSession: any) => {
      setActiveSessionRunning(true);
      // Push media files to the Appetize session after it starts
      const mediaFilesToPush = test?.settings?.mediaFiles ?? [];
      if (mediaFilesToPush.length > 0) {
        try {
          const { pushMediaToAppetize } = await import("@/utils/media-helpers");
          await pushMediaToAppetize(newSession, mediaFilesToPush);
        } catch (e) {
          console.error("Failed to push media files to Appetize session:", e);
        }
      }
    });
    session?.getData?.()?.on("end", (event: any) => {
      queryClient.invalidateQueries({ queryKey: ["deviceScreen"] });
      setActiveSessionRunning(false);
      setDeviceContextState({
        uiInspectorEnabled: false,
        recordInteractionsEnabled: false,
      });
    });
    if (companyData?.settings?.enableAppetizeCloneEditor) {
      setActiveSessionRunning(gptDriverSimulator.data != null);
    }
  }, [
    session,
    appetizeClient,
    gptDriverSimulator,
    companyData,
    setDeviceContextState,
    queryClient,
  ]);

  return (
    <>
      {companyData != null && (
        <>
          <CropScreenshotDialog />
          <ResetDialog
            open={openResetDialog}
            setOpen={setOpenResetDialog}
            sessionResetHandler={sessionResetHandler}
            appResetHandler={
              companyData.settings.enableAppetizeCloneEditor
                ? null
                : appResetHandler
            }
          />
          <DeviceWrapper
            setDeviceContextState={setDeviceContextState}
            startGptDriverSession={startGptDriverSession}
            companyData={companyData}
            sessionConfig={sessionConfig}
            deviceContextState={deviceContextState}
            test={test}
            buildData={buildData}
          />
          <ActionsWrapper>
            <Tooltip title={"Create a template"} placement="left">
              <span>
                <ActionButton
                  onClick={() => {
                    saveScreenshot.mutate();
                  }}
                  disabled={saveScreenshot.isPending || !activeSessionRunning}
                >
                  {saveScreenshot.isPending ? (
                    <CircularProgress size={"24px"} />
                  ) : (
                    <CameraAlt />
                  )}
                </ActionButton>
              </span>
            </Tooltip>
            <DeepLinkDialog
              activeSessionRunning={activeSessionRunning}
              session={session}
              companyData={companyData}
              gptDriverSimulator={gptDriverSimulator.data}
            />
            {!companyData.settings.enableAppetizeCloneEditor && (
              <Tooltip title={"Rotate device"} placement="left">
                <span>
                  <ActionButton
                    onClick={rotateHandler}
                    disabled={!activeSessionRunning}
                  >
                    <ScreenRotationIcon />
                  </ActionButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={"Show restart options"} placement="left">
              <span>
                <ActionButton
                  onClick={handleOpenResetDialog}
                  disabled={!activeSessionRunning}
                >
                  <Autorenew />
                </ActionButton>
              </span>
            </Tooltip>
            {companyData.settings.displayNetworkLogsOnEditor &&
              !companyData.settings.enableAppetizeCloneEditor && (
                <Tooltip title={"Display network logs"} placement="left">
                  <ActionButton
                    disabled={!activeSessionRunning}
                    onClick={() => {
                      window.open(session?.data?.networkInspectorUrl, "_blank");
                    }}
                  >
                    <Storage />
                  </ActionButton>
                </Tooltip>
              )}
            {companyData.settings.useAppetizeCommands && (
              <Tooltip title={"Display UI inspector"} placement="left">
                <span>
                  <ActionButton
                    onClick={() => {
                      setDeviceContextState((prev) => {
                        if (
                          prev.uiInspectorEnabled &&
                          !prev.recordInteractionsEnabled
                        ) {
                          queryClient.removeQueries({
                            queryKey: ["deviceScreen"],
                          });
                        }

                        return {
                          ...prev,
                          uiInspectorEnabled: !prev.uiInspectorEnabled,
                        };
                      });
                    }}
                    disabled={
                      !activeSessionRunning ||
                      (deviceContextState?.recordInteractionsEnabled ?? false)
                    }
                  >
                    <LocationSearchingIcon
                      color={
                        deviceContextState.uiInspectorEnabled
                          ? "success"
                          : "inherit"
                      }
                    />
                  </ActionButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={"Record interactions"} placement="left">
              <span>
                <ActionButton
                  onClick={() => {
                    setDeviceContextState((prev) => {
                      if (
                        !prev.uiInspectorEnabled &&
                        prev.recordInteractionsEnabled
                      ) {
                        queryClient.removeQueries({
                          queryKey: ["deviceScreen"],
                        });
                      }

                      return {
                        ...prev,
                        recordInteractionsEnabled:
                          !prev.recordInteractionsEnabled,
                      };
                    });
                  }}
                  disabled={
                    !activeSessionRunning ||
                    (deviceContextState?.uiInspectorEnabled ?? false)
                  }
                >
                  <RadioButtonChecked
                    color={
                      deviceContextState.recordInteractionsEnabled
                        ? "success"
                        : "inherit"
                    }
                  />
                </ActionButton>
              </span>
            </Tooltip>

            {markdownMode && (
              <Tooltip
                title={
                  isRecording
                    ? "Stop recording"
                    : "Record the device + your voice to generate a test"
                }
                placement="left"
              >
                <span>
                  <ActionButton
                    onClick={() => {
                      if (isRecording) {
                        recorder.stop();
                      } else {
                        void recorder.start();
                      }
                    }}
                    disabled={!activeSessionRunning && !isRecording}
                  >
                    <Camera />
                  </ActionButton>
                </span>
              </Tooltip>
            )}

            <Tooltip title={"Go to OS home screen"}>
              <span>
                <ActionButton
                  disabled={
                    goToOSHomeMutation.isPending || !activeSessionRunning
                  }
                  onClick={() => {
                    goToOSHomeMutation.mutate();
                  }}
                >
                  {goToOSHomeMutation.isPending ? (
                    <CircularProgress size={"24px"} />
                  ) : (
                    <Home />
                  )}
                </ActionButton>
              </span>
            </Tooltip>
          </ActionsWrapper>
          {markdownMode && isRecording && (
            <RecordingOverlay
              elapsedMs={recorder.elapsedMs}
              hasAudio={recorder.hasAudio}
              onStop={recorder.stop}
            />
          )}
          {markdownMode && (
            <RecordingPreviewDialog
              open={previewOpen}
              recording={recording}
              organisationId={buildData.organisationId}
              onClose={() => {
                setPreviewOpen(false);
                setRecording(null);
              }}
              onRecordAgain={() => {
                setPreviewOpen(false);
                setRecording(null);
                void recorder.start();
              }}
            />
          )}
          {/* </DeviceWrapperAspectRatio> */}
        </>
      )}
    </>
  );
};

const ContentWrapper = styled.div`
  display: grid;
  grid-template-areas: "simulator editor";
  grid-template-columns: 45% 55%;
  background-color: #e8e8e8;
  height: 100%;
`;

const SimulatorSection = styled.div`
  grid-area: simulator;
  position: relative;
  padding-block: 32px;
  display: grid;
  //place-content: center;
`;

const ActionsWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ActionButton = styled.button`
  color: white;
  padding: 16px;
  border-radius: 9999px;
  background-color: #4c63ff;
  line-height: 0;

  &:disabled {
    color: #455a64;
    cursor: not-allowed;
    background-color: #bdbdbd;
  }

  &:hover {
    filter: drop-shadow(0 1px 2px rgb(0 0 0 / 0.1))
      drop-shadow(0 1px 1px rgb(0 0 0 / 0.06));
  }
`;

const EditorSection = styled.div`
  grid-area: editor;
  background-color: #313131;
  padding: 16px;
  overflow: auto;
`;

const SpinnerWrapper = styled.div`
  display: grid;
  place-content: center;
  height: 100%;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  height: 100%;
  width: 100%;
  display: none;
`;

const ScanningLine = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  height: 0.25rem;
  background-color: #22c55e; /* green-500 */
  @keyframes scanning {
    0% {
      top: 0;
    }
    50% {
      top: 100%;
    }
    100% {
      top: 0;
    }
  }
  animation: scanning 5s linear forwards;
`;

const ResetDialog = ({
  open,
  setOpen,
  sessionResetHandler,
  appResetHandler,
}: {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  sessionResetHandler: any;
  appResetHandler?: any;
}) => {
  const handleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, [setOpen]);
  const canRestartApp = appResetHandler != null;

  return (
    <Dialog fullWidth maxWidth={"sm"} open={open} onClose={handleOpen}>
      <DialogTitle>Restart option</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {canRestartApp
            ? "Do you want to restart the emulator or the app?"
            : "Do you want to restart the emulator?"}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant={"outlined"} onClick={sessionResetHandler}>
          Restart Emulator
        </Button>
        {canRestartApp && (
          <Button variant={"outlined"} onClick={appResetHandler}>
            Restart App
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const TestBuilderCustomAlert = () => {
  const [state, setState] = useAtom(testBuilderCustomAlertStateAtom);
  const closeHandler = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  // Automatically close after 3 seconds if autoclose is true
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (state.open && state.autoclose) {
      timer = setTimeout(() => {
        closeHandler();
      }, 3000);
    }

    return () => {
      if (timer != null) {
        clearTimeout(timer);
      }
    };
  }, [state.open, state.autoclose, closeHandler]);

  useEffect(() => {
    return () => {
      setState({ open: false });
    };
  }, []);

  return (
    <div
      className={`absolute z-20 pt-2 px-4 w-full ${
        state.open ? "visible" : "invisible"
      }`}
    >
      <Alert severity={state.type} onClose={closeHandler}>
        <AlertTitle>{state.title !== undefined && state.title}</AlertTitle>
        {state.description !== undefined && state.description}
        {state.listContent !== undefined && state.listContent.length > 0 && (
          <ul className="mt-2 ml-2 list-disc list-inside">
            {state.listContent.map((listItem, index) => (
              <li key={index}>{listItem}</li>
            ))}
          </ul>
        )}
      </Alert>
    </div>
  );
};

const testBuilderCustomAlertStateAtom = atomWithReset<{
  type?: "error" | "info" | "success" | "warning";
  title?: string;
  description?: string;
  listContent?: string[];
  open: boolean;
  autoclose?: boolean;
}>({
  open: false,
  autoclose: false,
});

export {
  TestBuilderPage,
  TestBuilderCustomAlert,
  testBuilderCustomAlertStateAtom,
  DeviceContextState,
};
